/**
 * Editor state: selected color, tool (as plain named values only), a
 * saved-state reference, and a bounded snapshot-based undo/redo history.
 *
 * History contract: one committed action (stroke, clear, resize, import, …)
 * produces exactly ONE history entry. Call beginStroke() before mutating the
 * document and commitStroke() after; the commit is skipped automatically when
 * nothing changed (snapshot equality), so no-op actions stay no-ops in history.
 *
 * Dirty contract: `dirty` reflects whether the current document differs from
 * the last saved/loaded state (`saved` snapshot). Every transition (commit,
 * undo, redo, document action) recomputes it, so undo/redo that returns the
 * document exactly to the saved content clears dirty; markSaved() refreshes
 * the saved reference to the current document.
 *
 * Snapshots are dimension-tagged (first two bytes: width, height; both ≤ 128),
 * so undo/redo also restores canvas size correctly after resize/import.
 *
 * Pure data + functions over that data. No DOM, no events.
 */

import { HISTORY_LIMIT } from "./limits.js";
import { PixelwerkstattError } from "./errors.js";
import { cloneRgba, rgbaEqual, type Rgba } from "./color.js";
import { PixelDocument } from "./document.js";


/** Named tools as simple values — the UI layer interprets them. */
export type ToolId = "pencil" | "eraser" | "fill" | "eyedropper";

/** Default palette color of the legacy app (#FFB454). */
export const DEFAULT_COLOR: Readonly<Rgba> = Object.freeze({ r: 255, g: 180, b: 84, a: 255 });

export interface EditorState {
  /** Selected paint color. */
  readonly color: Rgba;
  /** Currently selected tool id. */
  readonly tool: ToolId;
  /**
   * Dimension-tagged snapshot of the last saved/loaded document state.
   * `dirty` is always computed against this reference (see markSaved).
   */
  readonly saved: Snapshot;
  /**
   * True when the current document differs from `saved` (the last
   * saved/loaded state). Commit, undo, redo, and document actions all
   * recompute this; undo/redo can therefore clear dirty again when they
   * return the document exactly to the saved content.
   */
  readonly dirty: boolean;
  /** The working document (mutated in place by apply/undo/redo helpers). */
  readonly document: PixelDocument;
  readonly undo: readonly HistoryEntry[];
  readonly redo: readonly HistoryEntry[];
}

export interface HistoryEntry {
  /** Dimension-tagged snapshot of document data BEFORE the action. */
  readonly before: Snapshot;
  /** Dimension-tagged snapshot of document data AFTER the action. */
  readonly after: Snapshot;
  /** Human-readable label, e.g. "Stift" or "Leinwand geleert". */
  readonly label: string;
}

/** Dimension-tagged snapshot: bytes = [width, height, ...rgba data]. */
export type Snapshot = Uint8ClampedArray;

/** Create initial editor state around the given working document. */
export function createEditorState(
  doc: PixelDocument,
  color?: Rgba,
  tool: ToolId = "pencil",
): EditorState {
  return {
    color: color !== undefined ? cloneRgba(color) : { ...DEFAULT_COLOR },
    tool,
    dirty: false,
    saved: takeSnapshot(doc),
    document: doc,
    undo: [],
    redo: [],
  };
}

// ---------------------------------------------------------------------------
// Simple value updates (do not touch history)
// ---------------------------------------------------------------------------

export function setColor(state: EditorState, color: Rgba): EditorState {
  if (!rgbaEqual(state.color, color)) {
    return { ...state, color: cloneRgba(color) };
  }
  return state;
}

export function setTool(state: EditorState, tool: ToolId): EditorState {
  return state.tool !== tool ? { ...state, tool } : state;
}

/**
 * Mark the document as saved/loaded: refreshes the saved snapshot to the
 * current document content and clears the dirty flag. From this point on,
 * `dirty` becomes true again as soon as the document differs from the new
 * saved reference — including via undo/redo that leaves the saved content.
 */
export function markSaved(state: EditorState): EditorState {
  if (!state.dirty) return state; // document already equals saved; nothing to do
  return { ...state, saved: takeSnapshot(state.document), dirty: false };
}

/**
 * Recompute `dirty` from the saved snapshot versus the current document.
 * Internal invariant: `dirty === !snapshotsEqual(saved, current)`.
 */
function withDirty(state: EditorState): EditorState {
  return {
    ...state,
    dirty: !snapshotsEqual(state.saved, takeSnapshot(state.document)),
  };
}

// ---------------------------------------------------------------------------
// Snapshot helpers
// ---------------------------------------------------------------------------

/** Capture a dimension-tagged snapshot of the given document. */
export function takeSnapshot(doc: PixelDocument): Snapshot {
  const out = new Uint8ClampedArray(doc.data.length + 2);
  out[0] = doc.width;
  out[1] = doc.height;
  out.set(doc.data, 2);
  return out;
}

function snapshotsEqual(a: Snapshot, b: Snapshot): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Decode a snapshot into a fresh document. Throws on malformed snapshots. */
export function snapshotToDocument(snapshot: Snapshot): PixelDocument {
  if (snapshot.length < 2) {
    throw new PixelwerkstattError(
      "DOCUMENT_STATE",
      "Ungültiger Verlaufs-Snapshot: zu kurz.",
    );
  }
  const width = snapshot[0]!;
  const height = snapshot[1]!;
  const data = snapshot.slice(2);
  // PixelDocument.create validates dimensions and data length (invariants).
  return PixelDocument.create(width, height, data);
}

// ---------------------------------------------------------------------------
// Stroke/action commit protocol
// ---------------------------------------------------------------------------

/** Begin a potential action: capture the "before" snapshot. */
export function beginStroke(state: EditorState): Snapshot {
  return takeSnapshot(state.document);
}

/**
 * Commit an action: if the document changed since `before`, push exactly one
 * history entry (bounded to HISTORY_LIMIT) and set dirty. Returns the new
 * state; if nothing changed, the state is returned unchanged (same reference).
 */
export function commitStroke(
  state: EditorState,
  before: Snapshot,
  label: string,
): EditorState {
  const after = takeSnapshot(state.document);
  if (snapshotsEqual(before, after)) {
    return state; // no-op: no history entry, dirty unchanged
  }
  const entry: HistoryEntry = { before, after, label };
  const undo = [...state.undo, entry].slice(-HISTORY_LIMIT);
  return withDirty({ ...state, undo, redo: [] });
}

// ---------------------------------------------------------------------------
// Undo / redo
// ---------------------------------------------------------------------------

/**
 * Undo the last committed action. Restores the "before" snapshot into the
 * working document (in place, including dimensions). Returns the new state,
 * or null when there is nothing to undo.
 */
export function undo(state: EditorState): EditorState | null {
  const entry = state.undo[state.undo.length - 1];
  if (entry === undefined) return null;
  const restored = snapshotToDocument(entry.before);
  state.document.replaceWith(restored.width, restored.height, restored.data);
  return withDirty({
    ...state,
    undo: state.undo.slice(0, -1),
    redo: [...state.redo, entry],
  });
}

/**
 * Redo the last undone action. Restores the "after" snapshot into the
 * working document. Returns the new state, or null when there is nothing
 * to redo.
 */
export function redo(state: EditorState): EditorState | null {
  const entry = state.redo[state.redo.length - 1];
  if (entry === undefined) return null;
  const restored = snapshotToDocument(entry.after);
  state.document.replaceWith(restored.width, restored.height, restored.data);
  return withDirty({
    ...state,
    undo: [...state.undo, entry],
    redo: state.redo.slice(0, -1),
  });
}

// ---------------------------------------------------------------------------
// Whole-document actions (import / clear / resize), one history entry each
// ---------------------------------------------------------------------------

/**
 * Apply a whole-document change as one committed action. `mutate` receives a
 * scratch clone of the current document and returns the next document (or
 * null for "no change"). History, dirty flag, and redo clearing follow the
 * same rules as commitStroke.
 */
export function applyDocumentAction(
  state: EditorState,
  label: string,
  mutate: (scratch: PixelDocument) => PixelDocument | null,
): EditorState {
  const before = takeSnapshot(state.document);
  const result = mutate(state.document.clone());
  if (result === null) {
    return state;
  }
  const after = takeSnapshot(result);
  if (snapshotsEqual(before, after)) {
    return state;
  }
  state.document.replaceWith(result.width, result.height, result.data);
  const entry: HistoryEntry = { before, after, label };
  const undo = [...state.undo, entry].slice(-HISTORY_LIMIT);
  return withDirty({ ...state, undo, redo: [] });
}
